import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();
    const { isDarkMode } = useTheme();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await login(email, password);
            toast.success('Login successful!');
            navigate('/');
        } catch (error: any) {
            console.error('Login failed', error);
            const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`min-h-screen flex items-center justify-center content-spacing-default safe-area-inset-top safe-area-inset-bottom transition-colors duration-200 ${
            isDarkMode ? 'bg-black' : 'bg-gray-50'
        }`}>
            <div className="max-w-md w-full space-y-6 md:space-y-8">
                <div className="text-center">
                    <h2 className={`text-responsive-3xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Voice Assistant Dashboard
                    </h2>
                    <p className={`mt-2 text-responsive-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Sign in to access your call logs and analytics
                    </p>
                </div>
                <form className="mt-6 md:mt-8 space-y-4 md:space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="email" className={`block text-responsive-sm font-medium ${
                                isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Email address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className={`form-mobile mt-1 block w-full px-3 py-3 md:py-2 border rounded-md shadow-sm transition-colors duration-200 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                    isDarkMode 
                                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                                }`}
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className={`block text-responsive-sm font-medium ${
                                isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                className={`form-mobile mt-1 block w-full px-3 py-3 md:py-2 border rounded-md shadow-sm transition-colors duration-200 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                                    isDarkMode 
                                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                                }`}
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-mobile touch-target w-full flex justify-center py-3 md:py-2 px-4 border border-transparent rounded-md shadow-sm text-responsive-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                    </div>

                    <div className="text-center">
                        <p className={`text-responsive-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Contact your administrator for account access
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;
